import { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AiBackIcon, Spinner } from "components/atoms/Icon";
import { Typography } from "components/atoms";
import {
  DrillCard,
  Carousel,
  AiOverAllCard,
  HeaderNotification,
  FetchError,
  RefreshModal,
} from "components/molecules";
import { useScreenSize, useAppDispatch } from "hooks";
import {
  DASHBOARD_PROGRAME,
  MCAT_TEST_TYPE,
  MOBILE_WIDTH,
  SELF_PACED_PROGRAME,
  TAB_LARGE_WIDTH,
} from "utils/constants";
import { addSpaceBeforeCapitals } from "utils/functions/addSpaceBeforeCapitals";
import classNames from "classnames";
import { useGetAiRecommendationsQuery } from "services/aiRecommendations";
import { DrillModal } from "components/organisms/TabClassHomeWork";
import { formatTime } from "utils/functions/formatTime";
import { useLazyGetCourseIndexQuery } from "services/courseIndex";
import { useSegmentReFetch } from "context";
import {
  useGetCourseworkQuery,
  useGetOverallProgressQuery,
  useGetTestScoresAndPerformanceQuery,
} from "services/homeApi";
import { getObjectKeysByValue } from "services/helpers";
import { getTimeFilterInput } from "utils/functions/getTimeFilterInput";
import { decodeToken } from "utils/functions/decodeToken";
import { useLazyGetStudyPlannerEventsQuery } from "services/studyPlanner";
import {
  replaceArrayOfObjects,
  reStructureSegment,
} from "utils/functions/courseWorkReStructure";
import { findSegmentByContentObjectGuid } from "utils/functions/findSegmentByContentObjectGuid";
import useGoogleAnalytics from "hooks/useGoogleAnalytics";
import { feature_toggle } from "featureToggle";
import { 
  DRILLS_RECOMMENDED, 
  VIDEOS_AND_OFFLINE_RECOMMENDATIONS 
} from "utils/constants/GoogleTagGUIDs";
import { openExternalUrl } from "utils/functions/openExternalUrl";
import "./index.scss";

interface IProps {
  theme: "light" | "dark";
}

export const StudyRecommendationTool = ({ theme }: IProps) => {
  const [startIndex, setStartIndex] = useState(0);
  const [startDrillIndex, setStartDrillIndex] = useState(0);
  const [startAttemptCardIndex, setStartAttemptCardIndex] = useState(0);
  const [isVideoPlayerOpen, setIsVideoPlayerOpen] = useState(false);
  const [isVideoPlay, setIsVideoPlay] = useState(false);
  const [isIconShow, setIsIconShow] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [selectedUserFriendlyRate, setSelectedUserFriendlyRate] = useState(0);
  const [selectedAccuracyRate, setSelectedAccuracyRate] = useState(0);
  const [selectedRecommendRate, setSelectedRecommendRate] = useState(0);
  const screen = useScreenSize();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location;

  // Get URL parameters for persistence across refreshes
  const searchParams = new URLSearchParams(location.search);
  const topicFromUrl = searchParams.get('topic');
  const subjectFromUrl = searchParams.get('subject');

  // Preserve navigation state to survive history manipulations from iframe modal
  // Initialize from state or reconstruct from URL params
  const assessmentObjectiveRef = useRef(
    state?.data?.AssessmentObjective || 
    (topicFromUrl && subjectFromUrl ? {
      Parent: { Name: decodeURIComponent(topicFromUrl) },
      Subject: decodeURIComponent(subjectFromUrl)
    } : null)
  );

  const [courseWorkPageNo, setCourseWorkPageNo] = useState(1);

  const { data, setData, selectedBtn, selectedSession, subject } =
    useSegmentReFetch();

  const payload = decodeToken();

  const userId = payload ? payload.userid : null;
  const productCode = payload ? payload.productcode : null;
  const offeringCode = payload ? payload.offeringcode : null;
  const testtype =
    payload && payload.hasOwnProperty("testtype") && payload.testtype;

  const isSelfPaced =
    payload &&
    payload.hasOwnProperty("programtype") &&
    payload.programtype === SELF_PACED_PROGRAME;

  const isDashboard =
    payload &&
    payload.hasOwnProperty("programtype") &&
    payload.programtype === DASHBOARD_PROGRAME;

  // Initialize ref on mount if not set and update URL if needed
  useEffect(() => {
    if (state?.data?.AssessmentObjective && !assessmentObjectiveRef.current) {
      const objective = state.data.AssessmentObjective;
      assessmentObjectiveRef.current = objective;
      
      // Update URL with params for refresh persistence
      const topicName = objective.Parent?.Name;
      const subjectName = objective.Subject;
      
      if (topicName && subjectName) {
        const newSearchParams = new URLSearchParams();
        newSearchParams.set('topic', encodeURIComponent(topicName));
        newSearchParams.set('subject', encodeURIComponent(subjectName));
        
        // Only update if params are different
        if (location.search !== `?${newSearchParams.toString()}`) {
          navigate(`${location.pathname}?${newSearchParams.toString()}`, { 
            replace: true,
            state 
          });
        }
      }
    }
  }, [state, location, navigate]);

  const {
    data: aiRecommendData,
    isLoading: isAiRecommendLoading,
    isFetching: isAiRecommendFetching,
    isError: isAiRecommendError,
    refetch: aiRecommendRefetch,
  } = useGetAiRecommendationsQuery();

  const videoCardArray =
    aiRecommendData &&
    Array.isArray(aiRecommendData) &&
    aiRecommendData.length > 0 &&
    assessmentObjectiveRef.current
      ? aiRecommendData[0].RecommendedContentItems.filter(
          (item: any) =>
            item.AssessmentObjective.Parent.Name ===
            assessmentObjectiveRef.current.Parent.Name
        ).filter(
          (item: any) =>
            item.ContentObject["<ContentObjectType>k__BackingField"] === "Video" ||
            item.ContentObject["<ContentObjectType>k__BackingField"] ===
              "OfflineReading" ||
            item.ContentObject["<ContentObjectType>k__BackingField"] ===
              "OfflineDrill"
        )
      : [];

  const [triggerRefetchCourseIndex, refetchCourseIndexResult] =
    useLazyGetCourseIndexQuery();

  const { refetch: refetchCoursework, isFetching: isFetchingCoursework } =
    useGetCourseworkQuery({
      page: courseWorkPageNo,
      ...(isDashboard
        ? {
            selectedOption: parseInt(
              getObjectKeysByValue(selectedBtn, true)[0],
              10
            ),
          }
        : {}),
      ...(isDashboard
        ? {
            timeFilter:
              selectedSession === null
                ? 1
                : getTimeFilterInput(Object.values(selectedSession)[0]),
          }
        : {}),
      ...(isSelfPaced
        ? {
            subject: subject,
          }
        : {}),
    });

  const { refetch: refetchOverAllProgress } = useGetOverallProgressQuery({
    Userid: userId,
    Productcode: productCode,
    Offeringcode: offeringCode,
  });

  const [triggerRefetchStudyPlanerEvents] = useLazyGetStudyPlannerEventsQuery();

  const { refetch: refetchTestScores } = useGetTestScoresAndPerformanceQuery();

  const drillCardArray =
    aiRecommendData &&
    Array.isArray(aiRecommendData) &&
    aiRecommendData.length > 0 &&
    assessmentObjectiveRef.current
      ? aiRecommendData[0].RecommendedContentItems.filter(
          (item: any) =>
            item.AssessmentObjective.Parent.Name ===
            assessmentObjectiveRef.current.Parent.Name
        ).filter(
          (item: any) =>
            item.ContentObject["<ContentObjectType>k__BackingField"] !== "Video" &&
            item.ContentObject["<ContentObjectType>k__BackingField"] !==
              "OfflineReading" &&
            item.ContentObject["<ContentObjectType>k__BackingField"] !==
              "OfflineDrill"
        )
      : [];

  const overallItems =
    aiRecommendData &&
    Array.isArray(aiRecommendData) &&
    aiRecommendData.length > 0 &&
    assessmentObjectiveRef.current
      ? aiRecommendData[0].RecommendedContentItems.filter(
          (item: any) =>
            item.AssessmentObjective.Parent.Name ===
            assessmentObjectiveRef.current.Parent.Name
        )
      : [];

  const attemptCardArray = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10"];

  const [elementsPerSlide, setElementsPerSlide] = useState(3);

  const [drillPerSlide, setDrillPerSlide] = useState(5);
  const [attemptPerSlide, setAttemptPerSlide] = useState(3);

  const { trackEvent } = useGoogleAnalytics();

  useEffect(() => {
    if (screen.width <= 514) {
      setElementsPerSlide(1);
    } else if (screen.width <= 766) {
      setElementsPerSlide(2);
    } else if (screen.width <= 1005) {
      setElementsPerSlide(3);
    } else if (screen.width <= 1024) {
      setElementsPerSlide(3);
    } else if (screen.width <= 1100) {
      setElementsPerSlide(2);
    } else if (screen.width <= 1175) {
      setElementsPerSlide(2);
    } else if (screen.width <= 1350) {
      setElementsPerSlide(3);
    } else if (screen.width <= 1450) {
      setElementsPerSlide(3);
    } else {
      setElementsPerSlide(4);
    }
  }, [screen.width]);

  useEffect(() => {
    if (screen.width <= 514) {
      setAttemptPerSlide(1);
    } else if (screen.width <= 766) {
      setAttemptPerSlide(1);
    } else if (screen.width <= 1005) {
      setAttemptPerSlide(2);
    } else if (screen.width <= 1024) {
      setAttemptPerSlide(3);
    } else if (screen.width <= 1100) {
      setAttemptPerSlide(1);
    } else if (screen.width <= 1350) {
      setAttemptPerSlide(2);
    } else {
      setAttemptPerSlide(3);
    }
  }, [screen.width]);

  useEffect(() => {
    if (screen.width <= 514) {
      setDrillPerSlide(1);
    } else if (screen.width <= 766) {
      setDrillPerSlide(2);
    } else if (screen.width <= 1005) {
      setDrillPerSlide(3);
    } else if (screen.width <= 1024) {
      setDrillPerSlide(3);
    } else if (screen.width <= 1100) {
      setDrillPerSlide(2);
    } else if (screen.width <= 1175) {
      setDrillPerSlide(2);
    } else if (screen.width <= 1350) {
      setDrillPerSlide(3);
    } else if (screen.width <= 1450) {
      setDrillPerSlide(3);
    } else {
      setDrillPerSlide(4);
    }
  }, [screen.width]);

  const totalSlides = Array.isArray(videoCardArray)
    ? Math.ceil(videoCardArray.length / elementsPerSlide)
    : 0;
  const totalDrillSlides = Array.isArray(drillCardArray)
    ? Math.ceil(drillCardArray.length / drillPerSlide)
    : 0;
  const totalattemptSlides = Math.ceil(
    attemptCardArray.length / attemptPerSlide
  );

  const slides = [];
  for (let i = 0; i < totalSlides; i++) {
    const startIndex = i * elementsPerSlide;
    const endIndex = Math.min(
      startIndex + elementsPerSlide,
      videoCardArray.length
    );
    const slideElements = videoCardArray.slice(startIndex, endIndex);
    slides.push(slideElements);
  }

  const drillSlides = [];
  for (let i = 0; i < totalDrillSlides; i++) {
    const startIndex = i * drillPerSlide;
    const endIndex = Math.min(
      startIndex + drillPerSlide,
      drillCardArray.length
    );
    const slideElements = drillCardArray.slice(startIndex, endIndex);
    drillSlides.push(slideElements);
  }

  const attemptSlides = [];
  for (let i = 0; i < totalattemptSlides; i++) {
    const startIndex = i * attemptPerSlide;
    const endIndex = Math.min(
      startIndex + attemptPerSlide,
      attemptCardArray.length
    );
    const slideElements = attemptCardArray.slice(startIndex, endIndex);
    attemptSlides.push(slideElements);
  }

  const handleVideoCardNextClick = () => {
    if (startIndex + 4 >= videoCardArray.length) {
      setStartIndex(0);
    } else {
      setStartIndex((prevIndex) => prevIndex + 1);
    }
  };

  const handleDrillCardNextClick = () => {
    if (startDrillIndex + 5 >= drillCardArray.length) {
      setStartDrillIndex(0);
    } else {
      setStartDrillIndex((prevIndex) => prevIndex + 1);
    }
  };

  const handleAttemptCardNextClick = () => {
    if (startAttemptCardIndex + 2 >= attemptCardArray.length) {
      setStartAttemptCardIndex(0);
    } else {
      setStartAttemptCardIndex((prevIndex) => prevIndex + 1);
    }
  };

  const handleVideoOpen = () => {
    setIsVideoPlayerOpen(true);
  };

  const handleVideoClose = () => {
    setIsVideoPlayerOpen(false);
  };

  const handleBackRedirection = () => {
    if (isDashboard) {
      navigate("/", { state: { fromStudyRecommendationTool: true } });
    } else if (isSelfPaced) {
      navigate("/self-paced", { state: { fromStudyRecommendationTool: true } });
    }
  };

  const handelVideoPlay = () => {
    const video = document.getElementById(
      "video-player"
    ) as HTMLVideoElement | null;
    video?.play();
    setIsVideoPlay(true);
  };

  const handelVideoPause = () => {
    const video = document.getElementById(
      "video-player"
    ) as HTMLVideoElement | null;
    video?.pause();
    setIsVideoPlay(false);
  };

  const [openUpdateModalState, setOpenUpdateModalState] = useState<any>({
    isOpen: false,
    data: null,
  });

  const [isRefreshModalState, setIsRefreshModalState] = useState<any>({
    isOpen: false,
    type: null,
  });

  const handleUpdateModalClose = () => {
    setOpenUpdateModalState((prev: any) => ({
      ...prev,
      isOpen: false,
      data: null,
    }));
  };

  const handleRefreshModalClose = () => {
    setIsRefreshModalState((prev: any) => ({
      ...prev,
      isOpen: false,
      type: null,
    }));
  };

  const handleCloseFeedbackModal = () => {
    setShowFeedbackModal(false);
  };

  const findCourseworkPageFromItem = (contentObjectGuid: string) => {
    const selectedSegment = findSegmentByContentObjectGuid(
      data,
      contentObjectGuid
    );

    if (selectedSegment && selectedSegment.page) {
      setCourseWorkPageNo((prev) => selectedSegment.page);
    }
  };

  const handleDrillBtnClick = (element: any) => {
    if (
      element.ContentObject["<ContentObjectType>k__BackingField"] ===
        "OfflineReading" ||
      element.ContentObject["<ContentObjectType>k__BackingField"] ===
        "OfflineDrill"
    ) {
      if (screen.width <= MOBILE_WIDTH) {
        if (isDashboard) {
          navigate("/drill", {
            state: {
              ContentObjectGuid: element.ContentObject["<Guid>k__BackingField"],
              ContentObjectType:
                element.ContentObject["<ContentObjectType>k__BackingField"],
              Name: element.ContentObject["<Name>k__BackingField"],
            },
          });
        } else if (isSelfPaced) {
          navigate("/self-paced/drill", {
            state: {
              ContentObjectGuid: element.ContentObject["<Guid>k__BackingField"],
              ContentObjectType:
                element.ContentObject["<ContentObjectType>k__BackingField"],
              Name: element.ContentObject["<Name>k__BackingField"],
            },
          });
        }
      } else {
        findCourseworkPageFromItem(
          element.ContentObject["<Guid>k__BackingField"]
        );
        setOpenUpdateModalState((prev: any) => ({
          ...prev,
          isOpen: true,
          data: {
            ContentObjectGuid: element.ContentObject["<Guid>k__BackingField"],
            ContentObjectType:
              element.ContentObject["<ContentObjectType>k__BackingField"],
            Name: element.ContentObject["<Name>k__BackingField"],
            Status: element.ContentObjectStatus,
          },
        }));
      }
      // track google tag manager event
      feature_toggle.googleTagManager.isEnable === true &&
        trackEvent("button_click", "Start/Resume drills");
    } else {
      openExternalUrl({
        url: element.ContentObject["<Url>k__BackingField"],
        contentObjectType: element.ContentObject["<ContentObjectType>k__BackingField"],
        testType: testtype,
        dispatch,
        onRefreshModalOpen: (contentType) => {
          findCourseworkPageFromItem(
            element.ContentObject["<Guid>k__BackingField"]
          );
          setIsRefreshModalState((prev: any) => ({
            ...prev,
            isOpen: true,
            type:
              contentType ||
              element.ContentObject["<ContentObjectType>k__BackingField"],
          }));
        },
      });
      // track google tag manager event
      feature_toggle.googleTagManager.isEnable === true &&
        trackEvent("button_click", "Start/Resume Video/Offline");
    }
  };

  const handleRefetchAiRecommendation = async () => {
    const promises = [
      refetchCoursework(),
      aiRecommendRefetch(),
      triggerRefetchCourseIndex(),
      refetchOverAllProgress(),
      refetchTestScores(),
      ...(isDashboard ? [triggerRefetchStudyPlanerEvents()] : []),
    ];

    Promise.allSettled(promises).then((responses: any) => {
      if (responses[0].status === "fulfilled") {
        const deStructuredArray = reStructureSegment(responses[0].value.data);
        const updatedArray = replaceArrayOfObjects(deStructuredArray, data);
        setData([...updatedArray]);
      }
      handleUpdateModalClose();
    });

  };

  const createTitleElement = (element: any) => {
    const name = element.ContentObject["<Name>k__BackingField"];
    const hasTime =
      element.ContentObject["<PlannerEstimate>k__BackingField"] !== null &&
      element.ContentObject["<ContentObjectType>k__BackingField"] === "Video";

    const displayName = name.length > 25 ? name.substring(0, 25) + "..." : name;

    // Return JSX elements instead of string with HTML
    if (hasTime) {
      const timeString = formatTime(
        element.ContentObject["<PlannerEstimate>k__BackingField"]
      );
      return (
        <>
          {displayName} <span className="time-custom-css"> ({timeString})</span>
        </>
      );
    }

    return displayName;
  };

  useEffect(() => {
    if (screen.width <= TAB_LARGE_WIDTH && showFeedbackModal === true) {
      handleCloseFeedbackModal();
    }
    if (
      screen.width <= TAB_LARGE_WIDTH &&
      openUpdateModalState.isOpen === true
    ) {
      handleUpdateModalClose();
    }
  }, [screen.width]);

  useEffect(() => {
    const sliderEl: any = document.querySelector("#feedback-range");
    if (sliderEl) {
      sliderEl.style.background = `linear-gradient(to right, #329EE2 20%, #329EE2 20%)`;
    }
  }, [selectedRecommendRate]);

  return (
    <>
      <div className="study-recommendation-page-wrapper">
        <div className="content-container">
          <div className="content-wrapper">
            {isAiRecommendLoading ? (
              <Spinner theme={theme} />
            ) : isAiRecommendError ? (
              <div className={classNames("ai-charts-error-container")}>
                <FetchError content="Oops! failed to load resources." />
              </div>
            ) : (aiRecommendData &&
                Array.isArray(aiRecommendData) &&
                aiRecommendData.length === 0) ||
              (aiRecommendData &&
                Array.isArray(aiRecommendData) &&
                aiRecommendData.length > 0 &&
                aiRecommendData[0].RecommendedContentItems &&
                Array.isArray(aiRecommendData[0].RecommendedContentItems) &&
                aiRecommendData[0].RecommendedContentItems.length === 0) ? (
              <div className={classNames("ai-charts-null-container")}>
                <Typography label="No results" variant="h5Med" />
              </div>
            ) : (
              <>
                <div className="title-container">
                  <div className="title-section">
                    <div className="back-section">
                      <div
                        className="back-btn-typography"
                        onClick={handleBackRedirection}
                      >
                        <AiBackIcon
                          color={theme === "light" ? "#273E5A" : "#329EE2"}
                        />
                        <Typography label="Go back" variant="h6Underline" />
                      </div>
                      {screen.width > TAB_LARGE_WIDTH ? (
                        <div className="header-notification-section">
                          <HeaderNotification theme={theme} />
                        </div>
                      ) : null}
                    </div>
                    <div className="title">
                      <Typography
                        label={"AI Study Recommendations Tool"}
                        variant={
                          screen.width > TAB_LARGE_WIDTH ? "h1Bold" : "h3Med"
                        }
                        styles="title-title-custom-style"
                      />
                      <div className="beta-text-container">
                        <Typography
                          variant="h7Bold"
                          label="BETA*"
                          styles="beta-text-styles"
                        />
                      </div>
                    </div>
                    <div className="topic">
                      <Typography
                        label={`Topic: ${assessmentObjectiveRef.current?.Parent?.Name || 'Unknown'}`}
                        variant="h2Med"
                        styles="title-subject-custom-typography-style"
                      />
                    </div>
                  </div>
                  <div className="subject-section">
                    <Typography
                      label={`Subject: ${
                        assessmentObjectiveRef.current
                          ? addSpaceBeforeCapitals(
                              assessmentObjectiveRef.current.Subject
                            )
                          : ""
                      }`}
                      variant={
                        screen.width <= TAB_LARGE_WIDTH ? "h6Book" : "h4Med"
                      }
                    />
                  </div>
                </div>
                <div
                  className={classNames({
                    "video-container": true,
                    "no-video-results": slides.length === 0,
                  })}
                >
                  <div className="title-section">
                    <Typography
                      label={
                        screen.width >= MOBILE_WIDTH
                          ? "Videos and Offline Recommendations"
                          : "Videos Recommended for you"
                      }
                      variant="h4Bold"
                      styles="video-container-title-section-typography-custom-style"
                    />
                  </div>
                  <div className="video-section">
                    {slides.length === 0 ? (
                      <Typography
                        variant="h7Book"
                        label="No results"
                        styles="no-results-custom-text"
                      />
                    ) : (
                      <Carousel renderStyle="drill">
                        {slides.flat().map((element, index) => (
                          <div
                            key={index}
                            className={`video-card element-${index}`}
                          >
                            <DrillCard
                              key={index}
                              fullTitle={
                                element.ContentObject["<Name>k__BackingField"]
                              }
                              shouldShowTooltip={
                                element.ContentObject["<Name>k__BackingField"]
                                  .length > 25
                              }
                              title={createTitleElement(element)}
                              status={element.ContentObjectStatus}
                              type={
                                element.ContentObject[
                                  "<ContentObjectType>k__BackingField"
                                ] === "Video"
                                  ? "video"
                                  : "offline-drills"
                              }
                              onClick={() => handleDrillBtnClick(element)}
                              theme={theme}
                              widgetTrackingId={VIDEOS_AND_OFFLINE_RECOMMENDATIONS}
                            />
                          </div>
                        ))}
                      </Carousel>
                    )}
                  </div>
                </div>
                <div
                  className={classNames({
                    "drill-container": true,
                    "no-drills-results": drillSlides.length === 0,
                  })}
                >
                  <div className="title-section drill-title">
                    <Typography
                      variant="h4Bold"
                      label="Drills Recommended for you"
                      styles="drill-title-typography-custom-style"
                    />
                  </div>
                  {drillSlides.length === 0 ? (
                    <Typography
                      variant="h7Book"
                      label="No results"
                      styles="no-results-custom-text"
                    />
                  ) : (
                    <>
                      <div className="drill-card-section">
                        <Carousel  renderStyle="drill">
                          {drillSlides
                            .flat()
                            .map((element: any, index: number) => (
                              <div
                                key={index}
                                className={`video-card element-${index}`}
                              >
                                <DrillCard
                                  title={
                                    element.ContentObject[
                                      "<Name>k__BackingField"
                                    ] &&
                                    element.ContentObject[
                                      "<Name>k__BackingField"
                                    ].length > 30
                                      ? element.ContentObject[
                                          "<Name>k__BackingField"
                                        ].substring(0, 30) + "..."
                                      : element.ContentObject[
                                          "<Name>k__BackingField"
                                        ]
                                  }
                                  fullTitle={
                                    element.ContentObject[
                                      "<Name>k__BackingField"
                                    ]
                                  }
                                  shouldShowTooltip={
                                    element.ContentObject[
                                      "<Name>k__BackingField"
                                    ].length > 30
                                  }
                                  status={element.ContentObjectStatus}
                                  type={"drills"}
                                  onClick={() => handleDrillBtnClick(element)}
                                  theme={theme}
                                  widgetTrackingId={DRILLS_RECOMMENDED}
                                />
                              </div>
                            ))}
                        </Carousel>
                      </div>
                      <div className="over-all-card-section">
                        <AiOverAllCard
                          correctAnswers={overallItems
                            .map((item: any) => item.CorrectQuestionAttempts)
                            .filter((attempt: any) => attempt !== null)
                            .reduce(
                              (accumulator: number, currValue: number) =>
                                accumulator + currValue,
                              0
                            )}
                          questionsAttempted={overallItems
                            .map((item: any) => item.TotalAttempts)
                            .filter((attempt: any) => attempt !== null)
                            .reduce(
                              (accumulator: number, currValue: number) =>
                                accumulator + currValue,
                              0
                            )}
                          wrongAnswers={overallItems
                            .map((item: any) => item.IncorrectQuestionAttempts)
                            .filter((attempt: any) => attempt !== null)
                            .reduce(
                              (accumulator: number, currValue: number) =>
                                accumulator + currValue,
                              0
                            )}
                        />
                      </div>
                    </>
                  )}
                  {/* <div className="attempt-card-section">
                {screen.width >= MOBILE_WIDTH ? (
                  <Carousel>
                    {attemptSlides.map((slide, index) => (
                      <>
                        {slide.map((element, index) => (
                          <div
                            key={index}
                            className={`video-card element-${index}`}
                          >
                            <OverAllCard
                              isOverAll={false}
                              timeSectionLabel={`Attempt #${element}:`}
                              timeSectionData="mm/dd/yyyy"
                              questionSectionLabel="Questions attempted :"
                              questionSectionData="10"
                              correctSectionLabel="Correct answers :"
                              correctSectionData="1"
                              wrongSectionLabel="Wrong answers :"
                              wrongSectionData="9"
                              overAllSectionLabel="Overall result :"
                              overAllSectionData="10%"
                            />
                          </div>
                        ))}
                      </>
                    ))}
                  </Carousel>
                ) : (
                  attemptSlides.map((slide, index) => (
                    <>
                      {slide.map((element, index) => (
                        <div
                          key={index}
                          className={`video-card element-${index}`}
                        >
                          <OverAllCard
                            isOverAll={false}
                            timeSectionLabel={`Attempt #${element}:`}
                            timeSectionData="mm/dd/yyyy"
                            questionSectionLabel="Questions attempted :"
                            questionSectionData="10"
                            correctSectionLabel="Correct answers :"
                            correctSectionData="1"
                            wrongSectionLabel="Wrong answers :"
                            wrongSectionData="9"
                            overAllSectionLabel="Overall result :"
                            overAllSectionData="10%"
                          />
                        </div>
                      ))}
                    </>
                  ))
                )}
              </div> */}
                </div>
                <div className="disclaimer-text-wrapper">
                  <Typography
                    label="*"
                    variant="h9Book"
                    styles="asteric-styles"
                  />
                  <Typography
                    label="This feature is currently in beta testing, and we’re actively seeking feedback to improve it."
                    variant="h9Book"
                  />
                </div>
              </>
            )}
          </div>
        </div>
        {/* {screen.width >= TAB_LARGE_WIDTH ? (
          <Button
            label="Feedback?"
            onClick={() => setShowFeedbackModal(true)}
            type="scroll"
            icon={false}
            styles="custom-feedback-btn"
          />
        ) : null} */}
      </div>
      <DrillModal
        handleUpdateModalClose={handleUpdateModalClose}
        openUpdateModalState={openUpdateModalState}
        theme={theme}
        isFetchingOtherAPIs={
          isAiRecommendFetching ||
          refetchCourseIndexResult.isFetching ||
          isFetchingCoursework
        }
        refetchData={handleRefetchAiRecommendation}
      />
      <RefreshModal
        isOpen={isRefreshModalState.isOpen}
        handleOnClose={handleRefreshModalClose}
        theme={theme}
        contentObjectType={isRefreshModalState.type}
        isFetching={
          isAiRecommendFetching ||
          refetchCourseIndexResult.isFetching ||
          isFetchingCoursework
        }
        handleRefresh={handleRefetchAiRecommendation}
      />
      {/* <AiFeedbackModal
        onClose={handleCloseFeedbackModal}
        selectedAccuracyRate={selectedAccuracyRate}
        setSelectedAccuracyRate={setSelectedAccuracyRate}
        selectedRecommendRate={selectedRecommendRate}
        setSelectedRecommendRate={setSelectedRecommendRate}
        selectedUserFriendlyRate={selectedUserFriendlyRate}
        setSelectedUserFriendlyRate={setSelectedUserFriendlyRate}
        setShowFeedbackModal={setShowFeedbackModal}
        showFeedbackModal={showFeedbackModal}
        theme={theme}
      /> */}
      {/* <Modal isOpen={isVideoPlayerOpen} onClose={handleVideoClose}>
        <div className="video-player-wrapper">
          {!isVideoPlay && (
            <div
              className="play"
              onClick={handelVideoPlay}
              onMouseEnter={() => setIsIconShow(true)}
            >
              <VideoPlayIcon color="black" />
            </div>
          )}
          {isVideoPlay && (
            <div
              className="pause"
              onClick={handelVideoPause}
              onMouseEnter={() => setIsIconShow(true)}
            >
              <VideoPauseIcon />
            </div>
          )}
          {screen.width > 1024 ? (
            <div className="header">
              <div className="video-play-icon-text">
                <VideoPlayIcon color="white" />
                <Typography
                  variant="h9Book"
                  label="Electric Field"
                  styles="typography-custom-style"
                />
              </div>
              <div className="close-icon" onClick={handleVideoClose}>
                <VideoCloseIcon />
              </div>
            </div>
          ) : null}

          {screen.width <= 1024 ? (
            <div className="tab-header">
              <div className="title">
                <Typography
                  label="Physics Passage Strategy"
                  variant="h4Bold"
                  styles="custom-typography-style"
                />
              </div>

              <div className="close-icon" onClick={handleVideoClose}>
                <VideoCloseIcon />
              </div>
            </div>
          ) : null}
          <div
            className={classNames(
              screen.width > 1024 ? "web-player" : "tab-player"
            )}
            onMouseEnter={() => setIsIconShow(true)}
            onMouseLeave={() => setIsIconShow(false)}
          >
            <video
              width={screen.width > 1378 ? "1312" : "895"}
              height={screen.width > 1378 ? "532" : "638"}
              id="video-player"
              controls={screen.width <= 1024}
            >
              <source
                src="http://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4"
                type="video/mp4"
              />
            </video>
          </div>
          <div className="footer"></div>
        </div>
      </Modal> */}
    </>
  );
};
